import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { publicApi } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const initialState = {
  name: "",
  email: "",
  phone: "",
  service_type: "",
  address: "",
  description: "",
};

export default function QuoteForm() {
  const { settings } = useSettings();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const services = settings?.services || [];

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type || !form.address || !form.description) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.submitQuote(form);
      setSuccess(true);
      setForm(initialState);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      const text = typeof msg === "string" ? msg : "Failed to submit. Try again or call us.";
      toast.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 lg:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.28em] text-yellow-400 font-bold mb-4">
            Get a Quote
          </div>
          <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
            Free estimate.<br />Fast <span className="text-yellow-400">response</span>.
          </h2>
          <p className="text-white/70 text-lg leading-relaxed max-w-md mb-8">
            Tell us about your project. We'll respond within 24 hours with a clear, transparent quote — no surprises.
          </p>
          <div className="space-y-3">
            {[
              "Free, no-obligation quote",
              "Licensed & insured technicians",
              "Same-week scheduling available",
              "Transparent, upfront pricing",
            ].map((b) => (
              <div key={b} className="flex items-center gap-3 text-white/75">
                <div className="w-6 h-6 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5 text-yellow-400" />
                </div>
                <span className="text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-2 bg-yellow-400/5 blur-3xl rounded-2xl" />
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="relative bg-[#0d0d0f] border border-white/10 rounded-2xl p-7 lg:p-9 space-y-5"
            >
              {success ? (
                <div className="py-10 text-center" data-testid="quote-success">
                  <div className="w-16 h-16 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h3 className="font-display text-2xl text-white font-semibold tracking-tight mb-2">
                    Thanks — request received.
                  </h3>
                  <p className="text-white/60 mb-6">
                    We'll reach out within 24 hours. For urgent needs call {settings?.phone || "240-383-9887"}.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="quote-submit-another"
                    className="text-yellow-400 hover:underline text-sm font-medium"
                  >
                    Submit another request →
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full Name" testId="quote-name-field">
                      <Input
                        data-testid="quote-name-input"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Jane Smith"
                        className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40 focus-visible:border-yellow-400/50"
                      />
                    </Field>
                    <Field label="Phone" testId="quote-phone-field">
                      <Input
                        data-testid="quote-phone-input"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="(240) 555-0100"
                        className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40 focus-visible:border-yellow-400/50"
                      />
                    </Field>
                  </div>

                  <Field label="Email" testId="quote-email-field">
                    <Input
                      data-testid="quote-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="jane@example.com"
                      className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40 focus-visible:border-yellow-400/50"
                    />
                  </Field>

                  <Field label="Service Type" testId="quote-service-field">
                    <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                      <SelectTrigger
                        data-testid="quote-service-trigger"
                        className="bg-black/40 border-white/10 text-white h-11 focus:ring-yellow-400/40"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0d0d0f] border-white/10 text-white">
                        {services.map((s) => (
                          <SelectItem key={s.id} value={s.title} data-testid={`quote-service-option-${s.id}`}>
                            {s.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other" data-testid="quote-service-option-other">
                          Other / Not Sure
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Service Address" testId="quote-address-field">
                    <Input
                      data-testid="quote-address-input"
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                      placeholder="123 Main St, Silver Spring, MD"
                      className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40 focus-visible:border-yellow-400/50"
                    />
                  </Field>

                  <Field label="Project Description" testId="quote-description-field">
                    <Textarea
                      data-testid="quote-description-input"
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="Tell us about the work you need done…"
                      rows={4}
                      className="bg-black/40 border-white/10 text-white focus-visible:ring-yellow-400/40 focus-visible:border-yellow-400/50 resize-none"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="quote-submit-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-70 text-black font-bold px-6 py-4 rounded-md text-base transition-all hover:scale-[1.01] active:scale-[0.99] shadow-[0_0_35px_rgba(250,204,21,0.2)]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Quote Request
                      </>
                    )}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, children }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">{label}</Label>
      {children}
    </div>
  );
}
